import type {
  ProductCreateInput,
  ProductDTO,
  ProductImageCreateInput,
  ProductImageDTO,
  ProductImageReorderInput,
  ProductUpdateInput,
  ProductVariantDTO,
  VariantCreateInput,
  VariantUpdateInput,
} from "@/modules/products/domain/contracts";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type { ProductDTO, ProductImageDTO, ProductVariantDTO };

export const productsApi = {
  async list(
    query: OffsetPageQueryDTO & {
      search?: string;
      status?: "draft" | "active" | "archived";
      kind?: "retail" | "production" | "hybrid";
      isSellable?: boolean;
      isShowcaseEligible?: boolean;
      categoryIds?: string[];
      tagIds?: string[];
    },
  ): Promise<OffsetPageDTO<ProductDTO>> {
    const url = new URL(`${config.apiUrl}/catalog/products`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    if (query.search) url.searchParams.set("search", query.search);
    if (query.status) url.searchParams.set("status", query.status);
    if (query.kind) url.searchParams.set("kind", query.kind);
    if (query.isSellable !== undefined)
      url.searchParams.set("isSellable", String(query.isSellable));
    if (query.isShowcaseEligible !== undefined)
      url.searchParams.set("isShowcaseEligible", String(query.isShowcaseEligible));
    if (query.categoryIds)
      url.searchParams.set("categoryIds", JSON.stringify(query.categoryIds));
    if (query.tagIds)
      url.searchParams.set("tagIds", JSON.stringify(query.tagIds));
    url.searchParams.set("include", JSON.stringify(["images"]));
    return fetcher.get<OffsetPageDTO<ProductDTO>>(url.toString());
  },
  async get(id: string): Promise<ProductDTO> {
    return fetcher.get<ProductDTO>(`${config.apiUrl}/catalog/products/${id}`);
  },
  async create(input: ProductCreateInput): Promise<ProductDTO> {
    return fetcher.post<ProductDTO>(`${config.apiUrl}/catalog/products`, input);
  },
  async update(id: string, input: ProductUpdateInput): Promise<ProductDTO> {
    return fetcher.put<ProductDTO>(
      `${config.apiUrl}/catalog/products/${id}`,
      input,
    );
  },
  async remove(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/catalog/products/${id}`,
    );
  },

  // Variants
  async generateVariants(
    productId: string,
    body?: {
      price?: number;
      cost?: number;
      compareAtPrice?: number;
      weightGrams?: number;
      lowStockThreshold?: number | null;
    },
  ): Promise<{ created: number; total: number; skipped: number }> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/variants/generate`,
      body ?? {},
    );
  },
  async listVariants(productId: string): Promise<ProductVariantDTO[]> {
    return fetcher.get(
      `${config.apiUrl}/catalog/products/${productId}/variants`,
    );
  },
  async createVariant(
    productId: string,
    data: Omit<VariantCreateInput, "productId">,
  ): Promise<ProductVariantDTO> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/variants`,
      data,
    );
  },
  async updateVariant(
    productId: string,
    variantId: string,
    data: VariantUpdateInput,
  ): Promise<ProductVariantDTO> {
    return fetcher.put(
      `${config.apiUrl}/catalog/products/${productId}/variants/${variantId}`,
      data,
    );
  },
  async bulkUpdateVariants(
    productId: string,
    body: { variantIds: string[]; payload: VariantUpdateInput },
  ): Promise<{ updated: number; variants: ProductVariantDTO[] }> {
    return fetcher.patch(
      `${config.apiUrl}/catalog/products/${productId}/variants/bulk`,
      body,
    );
  },
  async deleteVariant(productId: string, variantId: string): Promise<void> {
    return fetcher.delete(
      `${config.apiUrl}/catalog/products/${productId}/variants/${variantId}`,
    );
  },
  async bulkDeleteVariants(
    productId: string,
    body: { variantIds: string[] },
  ): Promise<{ deleted: number }> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/variants/bulk-delete`,
      body,
    );
  },

  // Images
  async listImages(productId: string): Promise<ProductImageDTO[]> {
    return fetcher.get(`${config.apiUrl}/catalog/products/${productId}/images`);
  },
  async createImage(
    productId: string,
    data: Omit<ProductImageCreateInput, "productId">,
  ): Promise<ProductImageDTO> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/images`,
      data,
    );
  },
  async createImagesBulk(
    productId: string,
    data: { fileKeys: string[]; blurhashes?: string[] },
  ): Promise<ProductImageDTO[]> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/images/bulk`,
      data,
    );
  },
  async deleteImage(productId: string, imageId: string): Promise<void> {
    return fetcher.delete(
      `${config.apiUrl}/catalog/products/${productId}/images/${imageId}`,
    );
  },
  async bulkDeleteImages(
    productId: string,
    body: { imageIds: string[] },
  ): Promise<{ deleted: number }> {
    return fetcher.post(
      `${config.apiUrl}/catalog/products/${productId}/images/bulk-delete`,
      body,
    );
  },
  async reorderImages(
    productId: string,
    data: ProductImageReorderInput,
  ): Promise<void> {
    return fetcher.put(
      `${config.apiUrl}/catalog/products/${productId}/images/reorder`,
      data,
    );
  },
};
